import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { prospectAnalytics } from "@/lib/api";
import { ArrowLeft, Clock, Eye, RefreshCw } from "lucide-react";

const SLIDE_TITLES = [
  "Title", "The Problem", "The Stakes", "The Real Question", "Meet Your Guide",
  "The Plan", "Closed Systems", "vs. DIY + AI", "Workflows", "The Team",
  "Trusted By", "Without / With", "Enterprise", "Calculator", "FAQ", "Let's Talk",
];

const SECTION_LABELS = {
  hero: "Hero / opening",
  diligence: "Compliance & legal (partnership)",
  calendar: "Regulatory calendar",
  heard: "What we heard",
  quickwins: "Quick-win selector",
  timeline: "First 45 days",
  commitment: "45-day commitment",
  builder: "Proposal builder",
  faq: "FAQ",
  expert: "Expert / testimonials",
  platform: "The Platform",
  privacy: "Data privacy",
  inside: "Inside the platform",
  path: "How it works",
  pricing: "Pricing",
  suite: "The full suite",
  build: "Engagement builder",
  courses: "Courses / training",
  tracking: "Engagement tracking",
  team: "The team",
  samples: "Samples",
  contact: "Contact",
  why: "Why us",
  calculator: "Calculator",
  tryit: "Examples",
};

const sectionLabel = (id) => SECTION_LABELS[id] || id.charAt(0).toUpperCase() + id.slice(1);

function fmtDuration(s) {
  if (!s) return "0s";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec}s`;
  return `${m}m ${sec}s`;
}

export default function AdminProspectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      setData(await prospectAnalytics(id));
    } catch {
      setData(null);
    } finally {
      setBusy(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const maxSec = Math.max(1, ...(data?.per_slide || []).map((s) => s.seconds || 0));

  return (
    <div className="min-h-screen bg-cream-50 text-ink-900 grain" data-testid="prospect-detail">
      <header className="flex items-center justify-between px-8 md:px-14 py-7 relative z-10">
        <button onClick={() => nav("/admin")} data-testid="back-to-admin"
                className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-500 transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> All prospects
        </button>
        <button onClick={load} data-testid="refresh-analytics"
                className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-500 transition-colors">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <main className="px-8 md:px-14 pb-20 relative z-10 max-w-5xl mx-auto">
        {!data ? (
          <div className="mono text-sm text-ink-900/55 mt-12">{busy ? "loading…" : "Not found."}</div>
        ) : (
          <>
            <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-500 mb-3">/ analytics</p>
            <h1 className="serif font-light text-5xl md:text-6xl tracking-tight leading-tight">
              {data.prospect.company_name}
            </h1>
            <p className="mt-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/55">/p/{data.prospect.slug}</p>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4">
              <Stat label="Visits" value={data.prospect.view_count || 0} icon={Eye} />
              <Stat label="Total time" value={fmtDuration(data.prospect.total_seconds || 0)} icon={Clock} />
              <Stat label="Last viewed" value={data.prospect.last_viewed_at ? new Date(data.prospect.last_viewed_at).toLocaleString() : "—"} icon={null} />
            </div>

            {(data.per_section || []).length > 0 && (
              <div className="mt-12" data-testid="section-heatmap">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1">/ section heatmap</div>
                <p className="text-sm text-ink-900/60 mb-4">What they actually read — total time spent with each section on screen.</p>
                <div className="space-y-2.5">
                  {(() => {
                    const maxSecSection = Math.max(1, ...data.per_section.map((s) => s.seconds || 0));
                    return data.per_section.map((row) => {
                      const pct = Math.max(2, (row.seconds / maxSecSection) * 100);
                      return (
                        <div key={row.section} className="border border-ink-900/10 rounded bg-white/55 p-4" data-testid={`section-stat-${row.section}`}>
                          <div className="flex items-baseline justify-between mb-2">
                            <span className="serif text-lg">{sectionLabel(row.section)}</span>
                            <span className="mono text-xs text-sun-500">{fmtDuration(row.seconds)}</span>
                          </div>
                          <div className="h-1.5 bg-cream/10 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-sun-700 to-sun-400 transition-all duration-500"
                                 style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            <div className="mt-12">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-4">/ time per slide</div>
              {(data.per_slide || []).length === 0 ? (
                <div className="border border-ink-900/10 rounded-md p-10 text-center bg-white/55 text-ink-900/55 mono text-sm">
                  No slide views yet.
                </div>
              ) : (
                <div className="space-y-2.5">
                  {SLIDE_TITLES.map((title, idx) => {
                    const row = (data.per_slide || []).find((r) => r.slide_index === idx);
                    const sec = row?.seconds || 0;
                    const views = row?.views || 0;
                    const pct = (sec / maxSec) * 100;
                    return (
                      <div key={title} className="border border-ink-900/10 rounded bg-white/55 p-4" data-testid={`slide-stat-${idx}`}>
                        <div className="flex items-baseline justify-between mb-2">
                          <div className="flex items-baseline gap-3">
                            <span className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">{String(idx + 1).padStart(2, "0")}</span>
                            <span className="serif text-lg">{title}</span>
                          </div>
                          <div className="flex items-baseline gap-4 mono text-xs text-ink-900/65">
                            <span>{views} view{views === 1 ? "" : "s"}</span>
                            <span className="text-sun-500">{fmtDuration(sec)}</span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-cream/10 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-sun-700 to-sun-400 transition-all duration-500"
                               style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="border border-ink-900/10 rounded-md p-5 bg-white/55">
      <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-2">
        {Icon ? <Icon className="h-3 w-3" /> : null} {label}
      </div>
      <div className="serif text-3xl text-sun-500">{value}</div>
    </div>
  );
}
